import * as XLSX from 'xlsx-js-style';

// This order mirrors the BOM table. Image/file fields are intentionally not
// included: downloads contain component data only.
export const BOM_EXPORT_COLUMNS = [
  { header: '#', field: (_, index) => index + 1, width: 6, numberFormat: '0', isSerialNumber: true },
  { header: 'Part Articles', field: part => part.partName ?? part.articleName, width: 24 },
  { header: 'Part Description', field: part => part.partDescription ?? part.articleName ?? part.description, width: 34 },
  { header: 'Stock Type', field: part => part.stockType, width: 14 },
  { header: 'Part Color', field: part => part.partColor, width: 18 },
  { header: 'Measurement Unit', field: part => part.measurementUnit, width: 20 },
  { header: 'L (Length)', field: part => part.length, width: 13, numberFormat: '0.00' },
  { header: 'W (Width)', field: part => part.width, width: 13, numberFormat: '0.00' },
  { header: 'H (Height)', field: part => part.height, width: 13, numberFormat: '0.00' },
  { header: 'Weight Unit', field: part => part.weightUnit, width: 15 },
  { header: 'Weight', field: part => part.partWeight, width: 13, numberFormat: '0.00' },
  { header: 'Category', field: part => part.category, width: 28 },
  { header: 'Vendor Part Number', field: part => part.vendorPartNumber, width: 24 },
  { header: "Vendor's Factory Name", field: part => part.vendorFactoryName, width: 28 },
  { header: 'Supplier Name', field: part => part.supplierName, width: 24 },
  { header: 'Supplier Part Number', field: part => part.supplierPartNumber, width: 26 },
  { header: 'UoM', field: part => part.uom, width: 12 },
  { header: 'Total Qty in FG', field: part => part.totalQtyInFg, width: 18, numberFormat: '0' },
  { header: 'Cost per UoM (USD)', field: part => part.costPerUom ?? part.unitPrice, width: 21, numberFormat: '$#,##0.00' },
];

const FORMULA_PREFIX = /^[=+\-@]/;

function toExcelValue(value, numberFormat) {
  if (value == null) return '';

  if (numberFormat && String(value).trim() !== '') {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
  }

  // Treat all data originating from users as text where appropriate. This
  // prevents a part number or name beginning with '=' from becoming a formula
  // when a recipient opens the workbook.
  if (typeof value === 'string') {
    return FORMULA_PREFIX.test(value) ? `'${value}` : value;
  }

  return value;
}

function safeFileSegment(value) {
  return String(value || 'BOM_Components')
    .trim()
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80) || 'BOM_Components';
}

/**
 * Creates an image-free Excel copy of the submitted BOM components.
 * `components` may be API part-article data or the vendor form's local rows.
 */
export function downloadBomComponentsExcel({ components, finishedGoodCode }) {
  const rows = (components || []).map((part, index) =>
    BOM_EXPORT_COLUMNS.map(column => toExcelValue(
      column.field(part, index),
      column.numberFormat
    ))
  );
  const fgCodeLabel = String(finishedGoodCode ?? '').trim();
  const worksheet = XLSX.utils.aoa_to_sheet([
    [fgCodeLabel ? `FG Code: ${fgCodeLabel}` : 'FG Code'],
    BOM_EXPORT_COLUMNS.map(column => column.header),
    ...rows,
  ]);

  worksheet['!cols'] = BOM_EXPORT_COLUMNS.map(column => ({ wch: column.width }));
  worksheet['!rows'] = [{ hpt: 24 }, { hpt: 20 }];
  worksheet['!merges'] = [{
    s: { r: 0, c: 0 },
    e: { r: 0, c: BOM_EXPORT_COLUMNS.length - 1 },
  }];

  // The merged first row follows the requested report layout: the finished
  // good code is a title above the component table, not a repeated column.
  if (worksheet.A1) {
    worksheet.A1.s = {
      font: { bold: true, color: { rgb: 'FF111827' }, sz: 14 },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: { bottom: { style: 'thin', color: { rgb: 'FF111827' } } },
    };
  }

  // The BOM table uses a yellow serial-number column; all BOM field headers,
  // including Part Articles, use the matching blue and white header style.
  BOM_EXPORT_COLUMNS.forEach((column, columnIndex) => {
    const address = XLSX.utils.encode_cell({ r: 1, c: columnIndex });
    if (worksheet[address]) {
      worksheet[address].s = {
        font: { bold: true, color: { rgb: column.isSerialNumber ? 'FF000000' : 'FFFFFFFF' } },
        fill: { fgColor: { rgb: column.isSerialNumber ? 'FFFFE500' : 'FF1D4ED8' } },
        alignment: { horizontal: 'center' },
      };
    }
  });

  // Numeric values use two decimal places throughout, except the component
  // quantity which is always shown as a whole number.
  rows.forEach((_, rowIndex) => {
    BOM_EXPORT_COLUMNS.forEach((column, columnIndex) => {
      if (!column.numberFormat) return;
      const address = XLSX.utils.encode_cell({ r: rowIndex + 2, c: columnIndex });
      if (worksheet[address]) {
        worksheet[address].z = column.numberFormat;
        if (column.isSerialNumber) {
          worksheet[address].s = {
            font: { color: { rgb: 'FF000000' } },
            fill: { fgColor: { rgb: 'FFFFE500' } },
            alignment: { horizontal: 'center' },
          };
        }
      }
    });
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'BOM Components');

  const filename = `${safeFileSegment(finishedGoodCode)}_BOM_Components.xlsx`;
  XLSX.writeFile(workbook, filename, { compression: true });
  return filename;
}
